import { Injectable } from '@angular/core';
import type { OverrideReportConfigTemplateFieldValue } from '@ag-common-lib/public-api';
import { LoadOptions } from 'devextreme/common/data';
import { ReportingApiService } from './reporting-api.service';

const OVERRIDE_TEMPLATES_GRID_PAGE_SIZE = 50;

export interface OverrideTemplateField {
  key: string;
  value: OverrideReportConfigTemplateFieldValue;
  editable: boolean;
}

export interface OverrideTemplateData {
  fields: OverrideTemplateField[];
}

export interface OverrideTemplateResponse {
  id: number;
  name: string;
  data: OverrideTemplateData;
  enabled?: boolean;
  createdAt: string;
}

export interface OverrideTemplateGetListResponse {
  data: OverrideTemplateResponse[];
  totalCount?: number;
}

export interface OverrideTemplateDetailsResponse {
  data: OverrideTemplateResponse;
}

export interface OverrideTemplateMutatePayload {
  name: string;
  data: OverrideTemplateData;
}

export interface OverrideTemplateAssignmentResponse {
  id: number;
  display_name?: string | null;
  transactions_of?: string | null;
}

export interface OverrideTemplateAssignmentsGetListResponse {
  data?: OverrideTemplateAssignmentResponse[];
  mappings?: OverrideTemplateAssignmentResponse[];
  totalCount?: number;
}

@Injectable()
export class OverrideTemplatesService {
  private readonly basePath = '/api/override/template';
  private readonly assignmentsBasePath = '/api/override/agencies';

  constructor(private readonly reportingApiService: ReportingApiService) {}

  getList(
    loadOptions: LoadOptions | Record<string, unknown> | null | undefined,
  ): Promise<OverrideTemplateGetListResponse> {
    return this.reportingApiService.request<OverrideTemplateGetListResponse>(`${this.basePath}/get-list`, {
      method: 'POST',
      body: this.normalizeLoadOptions(loadOptions),
    });
  }

  async getById(id: number): Promise<OverrideTemplateResponse> {
    const response = await this.reportingApiService.request<OverrideTemplateDetailsResponse>(`${this.basePath}/${id}`);
    return response.data;
  }

  async create(payload: OverrideTemplateMutatePayload): Promise<OverrideTemplateResponse> {
    const response = await this.reportingApiService.request<OverrideTemplateDetailsResponse>(this.basePath, {
      method: 'POST',
      body: payload,
    });
    return response.data;
  }

  async update(id: number, payload: OverrideTemplateMutatePayload): Promise<OverrideTemplateResponse> {
    const response = await this.reportingApiService.request<OverrideTemplateDetailsResponse>(`${this.basePath}/${id}`, {
      method: 'PUT',
      body: payload,
    });
    return response.data;
  }

  delete(id: number): Promise<void> {
    return this.reportingApiService.request<void>(`${this.basePath}/${id}`, {
      method: 'DELETE',
    });
  }

  loadPdf(
    id: number,
    payload: {
      fields?: Array<{ key: string; value: unknown }>;
    } = {},
  ): Promise<Blob> {
    return this.reportingApiService.request<Blob>(`${this.basePath}/${id}/pdf`, {
      method: 'POST',
      body: {
        fields: payload.fields ?? [],
      },
      responseType: 'blob',
    });
  }

  getAssignments(
    loadOptions: LoadOptions | Record<string, unknown> | null | undefined,
  ): Promise<OverrideTemplateAssignmentsGetListResponse> {
    return this.reportingApiService.request<OverrideTemplateAssignmentsGetListResponse>(
      `${this.assignmentsBasePath}/get-list`,
      {
        method: 'POST',
        body: this.normalizeLoadOptions(loadOptions),
      },
    );
  }

  private normalizeLoadOptions(
    loadOptions: LoadOptions | Record<string, unknown> | null | undefined,
  ): Record<string, unknown> {
    if (!loadOptions || typeof loadOptions !== 'object') {
      return {
        skip: 0,
        take: OVERRIDE_TEMPLATES_GRID_PAGE_SIZE,
        sort: [
          {
            selector: 'id',
            desc: false,
          },
        ],
        filter: [],
      };
    }

    const normalizedLoadOptions = Object.entries(loadOptions as Record<string, unknown>).reduce<
      Record<string, unknown>
    >((result, [key, value]) => {
      if (value !== undefined) {
        result[key] = value;
      }

      return result;
    }, {});

    normalizedLoadOptions['skip'] ??= 0;
    normalizedLoadOptions['take'] ??= OVERRIDE_TEMPLATES_GRID_PAGE_SIZE;
    normalizedLoadOptions['sort'] ??= [
      {
        selector: 'id',
        desc: false,
      },
    ];
    normalizedLoadOptions['filter'] ??= [];

    delete normalizedLoadOptions['requireTotalCount'];

    return normalizedLoadOptions;
  }
}
